import { useEffect, useState } from "react";
import { Star, Trash2 } from "lucide-react";
import {
  centsFromInput,
  formatMoney,
  inputFromCents,
  taxFromNet,
} from "@mavatech/shared";
import { Button, ConfirmDialog, Dialog, Input, Select } from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";

interface State {
  open: boolean;
  articleId?: string | null;
}

export function ArticleDialog({
  state,
  onClose,
}: {
  state: State;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const editing = !!state.articleId;
  const detail = trpc.article.get.useQuery(
    { id: state.articleId! },
    { enabled: state.open && editing },
  );
  const suppliers = trpc.supplier.list.useQuery(undefined, {
    enabled: state.open,
  });
  const org = trpc.organization.get.useQuery(undefined, {
    enabled: state.open,
  });
  const taxRate =
    (org.data as { defaultTaxRate?: number } | undefined)?.defaultTaxRate ?? 20;

  const [f, setF] = useState<Record<string, string>>({});
  const [fav, setFav] = useState(false);
  // Lieferantenpreis-Formular
  const [spSupplier, setSpSupplier] = useState("");
  const [spNet, setSpNet] = useState("");
  const [spList, setSpList] = useState("");
  const [spNr, setSpNr] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!state.open) return;
    if (editing && detail.data) {
      const d = detail.data;
      setF({
        ownNumber: d.ownNumber ?? "",
        name: d.name,
        unit: d.unit,
        category: d.category ?? "",
        ean: d.ean ?? "",
        salePrice: inputFromCents(d.salePriceCents),
        description: d.description ?? "",
      });
      setFav(d.isFavorite);
    } else if (!editing) {
      setF({
        ownNumber: "",
        name: "",
        unit: "Stk.",
        category: "",
        ean: "",
        salePrice: "0,00",
        description: "",
      });
      setFav(false);
    }
  }, [state.open, editing, detail.data]);

  const refresh = () => {
    utils.article.list.invalidate();
    if (state.articleId) utils.article.get.invalidate({ id: state.articleId });
  };
  const onSaved = () => {
    refresh();
    addToast({ type: "success", title: "Gespeichert" });
    if (!editing) onClose();
  };
  const onSaveErr = (e: { message: string }) =>
    addToast({ type: "error", title: "Fehler", description: e.message });
  const createM = trpc.article.create.useMutation({
    onSuccess: onSaved,
    onError: onSaveErr,
  });
  const updateM = trpc.article.update.useMutation({
    onSuccess: onSaved,
    onError: onSaveErr,
  });
  const save = editing ? updateM : createM;
  const setPrice = trpc.article.setSupplierPrice.useMutation({
    onSuccess: () => {
      setSpNet("");
      setSpList("");
      setSpNr("");
      refresh();
    },
  });
  const removePrice = trpc.article.removeSupplierPrice.useMutation({
    onSuccess: refresh,
  });
  const removeArticle = trpc.article.remove.useMutation({
    onSuccess: () => {
      utils.article.list.invalidate();
      addToast({ type: "success", title: "Artikel gelöscht" });
      onClose();
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });

  const onSave = () => {
    const base = {
      ownNumber: f.ownNumber?.trim() || null,
      name: f.name?.trim() || "Artikel",
      unit: f.unit?.trim() || "Stk.",
      category: f.category?.trim() || null,
      ean: f.ean?.trim() || null,
      salePriceCents: centsFromInput(f.salePrice ?? "0"),
      isFavorite: fav,
      description: f.description?.trim() || null,
    };
    if (editing) updateM.mutate({ id: state.articleId!, ...base } as never);
    else createM.mutate(base as never);
  };

  const prices = detail.data?.prices ?? [];

  return (
    <Dialog
      open={state.open}
      onOpenChange={(v) => !v && onClose()}
      title={editing ? "Artikel bearbeiten" : "Neuer Artikel"}
      wide
    >
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Bezeichnung"
          value={f.name ?? ""}
          onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))}
        />
        <Input
          label="Artikelnummer (intern)"
          value={f.ownNumber ?? ""}
          onChange={(e) => setF((s) => ({ ...s, ownNumber: e.target.value }))}
        />
        <Input
          label="Einheit"
          value={f.unit ?? ""}
          onChange={(e) => setF((s) => ({ ...s, unit: e.target.value }))}
        />
        <Input
          label="Warengruppe"
          value={f.category ?? ""}
          onChange={(e) => setF((s) => ({ ...s, category: e.target.value }))}
        />
        <Input
          label="EAN"
          value={f.ean ?? ""}
          onChange={(e) => setF((s) => ({ ...s, ean: e.target.value }))}
        />
        <Input
          label="Verkaufspreis netto (€)"
          inputMode="decimal"
          value={f.salePrice ?? ""}
          onChange={(e) => setF((s) => ({ ...s, salePrice: e.target.value }))}
        />
        <label className="col-span-2 flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={fav}
            onChange={(e) => setFav(e.target.checked)}
          />
          <Star
            className={`w-3.5 h-3.5 ${fav ? "text-accent fill-accent" : ""}`}
          />{" "}
          Favorit
        </label>
      </div>

      {editing && (
        <div className="mt-5 border-t border-border-subtle pt-4">
          <p className="text-sm font-semibold text-text-primary mb-2">
            Händlerpreise (Einkaufspreis-Vergleich)
          </p>
          <div className="space-y-1.5 mb-3">
            {prices.map((p, idx) => {
              const listGross =
                p.listPriceCents != null
                  ? p.listPriceCents + taxFromNet(p.listPriceCents, taxRate)
                  : null;
              return (
                <div
                  key={p.id}
                  className="flex items-center justify-between card p-2.5 text-sm"
                >
                  <span>
                    <span className="text-text-primary">{p.supplier.name}</span>
                    {p.supplierArticleNumber ? (
                      <span className="text-text-tertiary text-xs ml-2">
                        Nr. {p.supplierArticleNumber}
                      </span>
                    ) : null}
                    {idx === 0 &&
                    prices.length > 1 &&
                    p.netPriceCents != null ? (
                      <span className="text-2xs text-success ml-2">
                        günstigster
                      </span>
                    ) : null}
                  </span>
                  <span className="flex items-center gap-4">
                    <span className="text-right leading-tight">
                      <span className="block text-2xs text-text-tertiary">
                        Listenpreis netto / brutto
                      </span>
                      <span className="text-xs text-text-secondary">
                        {p.listPriceCents != null
                          ? formatMoney(p.listPriceCents)
                          : "—"}
                        {" / "}
                        {listGross != null ? formatMoney(listGross) : "—"}
                      </span>
                    </span>
                    <span className="text-right leading-tight">
                      <span className="block text-2xs text-text-tertiary">
                        Einkaufspreis netto
                      </span>
                      <span className="text-money text-text-primary font-medium">
                        {p.netPriceCents != null
                          ? formatMoney(p.netPriceCents)
                          : "—"}
                      </span>
                    </span>
                    <button
                      className="text-text-tertiary hover:text-danger"
                      onClick={() =>
                        removePrice.mutate({
                          articleId: state.articleId!,
                          supplierId: p.supplier.id,
                        })
                      }
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </span>
                </div>
              );
            })}
            {!prices.length && (
              <p className="text-sm text-text-tertiary">
                Noch keine Händlerpreise.
              </p>
            )}
          </div>
          <div className="flex items-end gap-2">
            <Select
              label="Lieferant"
              value={spSupplier}
              onChange={(e) => setSpSupplier(e.target.value)}
              options={[
                { value: "", label: "— wählen —" },
                ...(suppliers.data ?? []).map((s) => ({
                  value: s.id,
                  label: s.name,
                })),
              ]}
            />
            <Input
              label="Listenpreis netto (€)"
              inputMode="decimal"
              value={spList}
              onChange={(e) => setSpList(e.target.value)}
            />
            <Input
              label="Einkaufspreis netto (€)"
              inputMode="decimal"
              value={spNet}
              onChange={(e) => setSpNet(e.target.value)}
            />
            <Input
              label="Händler-Art.-Nr."
              value={spNr}
              onChange={(e) => setSpNr(e.target.value)}
            />
            <Button
              variant="secondary"
              disabled={!spSupplier || (!spNet && !spList)}
              loading={setPrice.isPending}
              onClick={() =>
                setPrice.mutate({
                  articleId: state.articleId!,
                  supplierId: spSupplier,
                  netPriceCents: spNet.trim() ? centsFromInput(spNet) : null,
                  listPriceCents: spList.trim() ? centsFromInput(spList) : null,
                  supplierArticleNumber: spNr.trim() || null,
                })
              }
            >
              Preis speichern
            </Button>
          </div>
        </div>
      )}

      <div className="flex justify-between gap-2 mt-5">
        {editing ? (
          <Button variant="danger" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="w-3.5 h-3.5" /> Artikel löschen
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Schließen
          </Button>
          <Button variant="primary" onClick={onSave} loading={save.isPending}>
            Speichern
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Artikel löschen?"
        message="Der Artikel wird aus der Liste entfernt. Bereits erstellte Angebote/Rechnungen mit diesem Artikel bleiben unverändert."
        confirmLabel="Löschen"
        danger
        onConfirm={() => {
          if (state.articleId) removeArticle.mutate({ id: state.articleId });
        }}
      />
    </Dialog>
  );
}
